import { ref } from 'vue';
import { firestoreDB } from '@/firestore';
import { useEmailSender } from '@/composables/emails/useEmailSender';
import { API_ENDPOINTS } from '@/apiConfig';
import axiosClient from '@/axiosClient';
import { useRoute } from 'vue-router';

export function useComplaints() {
    const complaints = ref([]);
    const loading = ref(true);
    const selectedComplaint = ref(null);
    const publishingStates = ref({});
    const route = useRoute();

    // Fetch complaints based on route params or provided arguments
    const fetchComplaints = async (date = null) => {
        loading.value = true;
        try {
            // Check route params first
            const routeYear = route.params.year;
            const routeMonth = route.params.month;

            // Construct the date parameters for the API as Unix timestamps
            let startDate = '', endDate = '';
            
            // If route has year and month, use those (regardless of ticketId)
            if (routeYear && routeMonth) {
                const year = parseInt(routeYear);
                const month = parseInt(routeMonth) - 1; // JavaScript months are 0-indexed
                const start = new Date(year, month, 1);
                const end = new Date(year, month + 1, 0);
                startDate = Math.floor(start.getTime() / 1000);
                endDate = Math.floor(end.getTime() / 1000);
            } 
            // Otherwise, use the date parameter if provided
            else if (date) {
                const start = new Date(date.getFullYear(), date.getMonth(), 1);
                const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                startDate = Math.floor(start.getTime() / 1000);
                endDate = Math.floor(end.getTime() / 1000);
            } 
            // If no date info is provided, use current month
            else {
                const now = new Date();
                const start = new Date(now.getFullYear(), now.getMonth(), 1);
                const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                startDate = Math.floor(start.getTime() / 1000);
                endDate = Math.floor(end.getTime() / 1000);
            }

            // Use the endpoint from API_ENDPOINTS
            let url = API_ENDPOINTS.fetchTickets
                .replace('{startDate}', startDate)
                .replace('{endDate}', endDate);

            
            // Use axiosClient instead of fetch
            const response = await axiosClient.get(url);
            
            // The response is already in the format we need, just assign it directly
            complaints.value = response.data;

            // If there's a ticket ID in the route, select that complaint
            if (route.params.ticketId) {
                selectedComplaint.value = complaints.value.find(
                    c => c.id.toString() === route.params.ticketId.toString()
                );
            }

        } catch (error) {
            console.error('Error fetching complaints:', error);
            complaints.value = [];
        } finally {
            loading.value = false;
        }
    };

    // Get filtered complaints for ViewComplaints.vue
    const getFilteredComplaints = () => {
        return complaints.value.map(complaint => {
            // Check: 
            const hasUnpublishedPost = complaint.posts?.some(post => 
                post.mailer_status === false || post.mailer_status === undefined
            );
            
            // Check if any post has read_receipt=false
            const hasUnreadPost = complaint.posts?.some(post => 
                post.read_receipt === false
            );
            
            // Check if any post has read_acknowledge=false
            const hasPendingAcknowledge = complaint.posts?.some(post => 
                post.read_acknowledge === false
            );
            
            // Update: Add mailer_status to the complaint root
            return {
                ...complaint,
                mailer_status: !hasUnpublishedPost,
                hasUnpublishedPost,
                hasUnreadPost,
                hasPendingAcknowledge
            };
        }).sort((a, b) => {
            // Update: Sort by unpublished status (unpublished before published)
            if (a.hasUnpublishedPost !== b.hasUnpublishedPost) {
                return a.hasUnpublishedPost ? -1 : 1;
            }

            // Update: Sort by status (Open before Reconcile before Closed)
            const statusOrder = { 'pending': 0, 'reconcile': 1, 'closed': 2 };
            const statusComparison = statusOrder[a.status] - statusOrder[b.status];
            
            if (statusComparison !== 0) {
                return statusComparison;
            }
        });
    };

    const publishComplaint = async (complaint, postIndex) => {
        try {
            console.log('publishComplaint', complaint, postIndex);
            // Set publishing state to true
            publishingStates.value[`${complaint.id}-${postIndex}`] = true;

            // Send email (keep using the existing email sender)
            const { sendEmail } = useEmailSender();
            await sendEmail(
                //complaint.employee.email,
                'hussainbtk@gmail.com',
                `Complaint #${complaint.id} ${complaint.posts[postIndex].type}`,
                complaint
            );

            // Prepare data for the API
            const publishData = {
                ticket_id: complaint.id,
                post_index: postIndex,
                mailer_status: true,
                status: complaint.status,
                timestamp: new Date().toISOString()
            };

            // Call the API endpoint to publish the ticket
            await axiosClient.put(API_ENDPOINTS.publishTicket, publishData);

            // Fetch fresh complaints to get the updated data
            await fetchComplaints();
            
            // Update selectedComplaint with fresh data
            selectedComplaint.value = complaints.value.find(c => c.id === complaint.id);

        } catch (error) {
            console.error('Error publishing complaint:', error);
            throw error;
        } finally {
            publishingStates.value[`${complaint.id}-${postIndex}`] = false;
        }
    };

    // Function: Delete a complaint
    const deleteComplaint = async (complaintId) => {
        try {
            await firestoreDB.deleteDocument('complaints', complaintId);
            // Update: Remove the complaint from the local array
            complaints.value = complaints.value.filter(c => c.id !== complaintId);
        } catch (error) {
            console.error('Error deleting complaint:', error);
            throw error;
        }
    };

    const deletePost = async (complaint, postIndex) => {
        try {
            // Remove the post from the posts array
            const updatedPosts = [...complaint.posts];
            const deletedPost = updatedPosts[postIndex];
            updatedPosts.splice(postIndex, 1);

            // Update: Determine the new status based on the deleted post and current status
            let newStatus = complaint.status;
            
            // Check: If we're deleting a draft post
            if (!deletedPost.mailer_status) {
                if (deletedPost.type === 'Reconcile' && complaint.status === 'Open') {
                    newStatus = 'Closed';
                } else if (deletedPost.type === 'Initial' && complaint.status === 'Open') {
                    newStatus = 'Open';
                } else if (deletedPost.type === 'Close' && complaint.status === 'Closed') {
                    newStatus = 'Open';
                }
            }

            // Update: Update the document in Firestore with both posts and new status
            await firestoreDB.updateDocument('complaints', complaint.id, {
                posts: updatedPosts,
                status: newStatus
            });

            // Update: Update the local state
            const updatedComplaint = {
                ...complaint,
                posts: updatedPosts,
                status: newStatus
            };

            // Update: Update the complaints array
            complaints.value = complaints.value.map(c => 
                c.id === complaint.id ? updatedComplaint : c
            );

            // Update: Update selected complaint
            selectedComplaint.value = updatedComplaint;

        } catch (error) {
            console.error('Error deleting post:', error);
            throw error;
        }
    };

    return {
        complaints,
        loading,
        selectedComplaint,
        fetchComplaints,
        getFilteredComplaints,
        publishComplaint,
        publishingStates,
        deleteComplaint,
        deletePost
    };
}
